import React, {
  FunctionComponent,
  useState,
  useCallback,
  useEffect,
} from "react";
import { Grid, Box, TextField, MenuItem, Button, Alert, FormControl, Skeleton } from "@mui/material";
import DashboardLayout from "../../components/layout/layout";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetUserProfileById,
  useUpdateUserProfile,
} from "../../hooks/api/user";
import {
  useGetCities,
  useGetCountries,
  useGetStates,
} from "../../hooks/api/common";
import { ACTIVE_COLOR, INACTIVE_COLOR, StyledSelect, StyledTextField } from "../../components/ui/formInput";
import { RootState } from "../../store";
import { useSelector } from "react-redux";

type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  email: ValidateField;
  password: ValidateField;
  firstName: ValidateField;
  lastName: ValidateField;
  //title: ValidateField;
  //phone: ValidateField;
  //fax: ValidateField;
  //address: ValidateField;
  //address2: ValidateField;
  //city: ValidateField;
  //state: ValidateField;
  //zip: ValidateField;
  //country: ValidateField;
};

const EditProfile: FunctionComponent = () => {
  const userInfo = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();
  const { userId } = useParams();
  if (userId === "") {
    navigate("/dashboard");
  }
  const { data: countries } = useGetCountries();
  const { mutateAsync: getStates } = useGetStates();
  const { mutateAsync: getCities } = useGetCities();
  const { data: userProfile, isFetched: hasFetched } = useGetUserProfileById(userInfo.userId);
  const { mutateAsync: editProfile } = useUpdateUserProfile();

  const formValidation: ValidateFieldsState = {
    email: { invalid: false, error: "" },
    password: { invalid: false, error: "" },
    firstName: { invalid: false, error: "" },
    lastName: { invalid: false, error: "" },
    //title: { invalid: false, error: "" },
    //phone: { invalid: false, error: "" },
    //fax: { invalid: false, error: "" },
    //address: { invalid: false, error: "" },
    //address2: { invalid: false, error: "" },
    //city: { invalid: false, error: "" },
    //state: { invalid: false, error: "" },
    //zip: { invalid: false, error: "" },
    //country: { invalid: false, error: "" },
  };

  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>();
  const [stateList, setStateList] = useState<any[]>([]);
  const [cityList, setCityList] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [disabled, setDisabled] = useState<boolean>(false);

  const [validateFields, setValidateFields] =
    useState<ValidateFieldsState>(formValidation);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [title, setTitle] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [fax, setFax] = useState("");
  const [address, setAddr1] = useState("");
  const [address2, setAddr2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [country, setCountry] = useState("");

  const fetchStateCities = useCallback(
    async (countryCode: string, stateCode: string) => {
      const res = await getStates({ countryCode: countryCode });
      setStateList(res || []);

      const resCities = await getCities({
        countryCode: countryCode,
        stateCode: stateCode,
      });
      setCityList(resCities || []);
    },
    [getStates, getCities]
  );

  useEffect(() => {
    if (userProfile) {
      setEmail(userProfile.email || "");
      setFirstName(userProfile.firstName || "");
      setLastName(userProfile.lastName || "");
      setTitle(userProfile.title || "");
      setAddr1(userProfile.address1 || "");
      setAddr2(userProfile.address2 || "");
      setPhone(userProfile.phone || "");
      setFax(userProfile.fax || "");
      setCity(userProfile.city || "");
      setState(userProfile.state || "");
      setZip(userProfile.zip || "");
      setCountry(userProfile.country || "");
      setSelectedCountry(userProfile.country || "");
      if (userProfile.country && userProfile.state) {
        fetchStateCities(userProfile.country, userProfile.state);
      }
    }
  }, [userProfile, fetchStateCities]);

  // Add error handling
  useEffect(() => {
    if (error) {
      setError("Failed to load profile data. Please try again.");
    }
  }, [error]);

  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    if (!firstName?.trim()) {
      errors.firstName = { invalid: true, error: "First name is required" };
      valid = false;
    } else {
      errors.firstName = { invalid: false, error: "" };
    }

    if (!lastName?.trim()) {
      errors.lastName = { invalid: true, error: "Last name is required" };
      valid = false;
    } else {
      errors.lastName = { invalid: false, error: "" };
    }

    if (!email?.trim()) {
      errors.email = { invalid: true, error: "Email is required" };
      valid = false;
    } else if (!isValidEmail(email)) {
      errors.email = {
        invalid: true,
        error: "Please enter a valid email address",
      };
      valid = false;
    } else {
      errors.email = { invalid: false, error: "" };
    }

    // if (!phone?.trim()) {
    //   errors.phone = { invalid: true, error: "Phone is required" };
    //   valid = false;
    // } else if (phone?.trim() && !/^\+?[\d\s-]{7,15}$/.test(phone)) {
    //   errors.phone = {
    //     invalid: true,
    //     error: "Please enter a valid phone number",
    //   };
    //   valid = false;
    // } else {
    //   errors.phone = { invalid: false, error: "" };
    // }

    // if (!address?.trim()) {
    //   errors.address = { invalid: true, error: "Address is required" };
    //   valid = false;
    // } else {
    //   errors.address = { invalid: false, error: "" };
    // }

    // if (!country) {
    //   errors.country = { invalid: true, error: "Country is required" };
    //   valid = false;
    // } else if (!countries?.some((c: any) => c.iso2 === country)) {
    //   errors.country = {
    //     invalid: true,
    //     error: "Please select a valid country",
    //   };
    //   valid = false;
    // } else {
      
    //   errors.country = { invalid: false, error: "" };
    // }

    // if (!state) {
    //   errors.state = { invalid: true, error: "State is required" };
    //   valid = false;
    // } else if (!stateList?.some((s: any) => s.stateCode === state)) {
    //   errors.state = { invalid: true, error: "Please select a valid state" };
    //   valid = false;
    // } else {
    //   errors.state = { invalid: false, error: "" };
    // }

    // if (!city) {
    //   errors.city = { invalid: true, error: "City is required" };
    //   valid = false;
    // } else if (!cityList?.some((c: any) => c.id === city)) {
    //   errors.city = { invalid: true, error: "Please select a valid city" };
    //   valid = false;
    // } else {
    //   errors.city = { invalid: false, error: "" };
    // }

    // if (!zip?.trim()) {
    //   errors.zip = { invalid: true, error: "ZIP code is required" };
    //   valid = false;
    // } else if (!/^\d{5}(-\d{4})?$/.test(zip)) {
    //   errors.zip = {
    //     invalid: true,
    //     error: "Please enter a valid ZIP code (e.g. 12345 or 12345-6789)",
    //   };
    //   valid = false;
    // } else {
    //   errors.zip = { invalid: false, error: "" };
    // }

    // if (fax?.trim() && !/^\+?[\d\s-]{7,15}$/.test(fax)) {
    //   errors.fax = { invalid: true, error: "Please enter a valid fax number" };
    //   valid = false;
    // } else {
    //   errors.fax = { invalid: false, error: "" };
    // }

    setValidateFields(errors);
    return valid;
  };

  const handleChangePassword = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/change-password");
  };

  const handleCancel = () => {
    navigate("/dashboard");
  };

  const handleFormAction = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setValidateFields(formValidation);
    if (!validateForm()) {
      return;
    }
    try {
      setDisabled(true);
      const result = await editProfile({
        userId: userProfile.userId.toString(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        title: title.trim(),
        email: email.toLowerCase().trim(),
        phone: phone.trim(),
        fax: fax.trim(),
        address: address.trim(),
        address2: address2.trim(),
        city: city,
        state: state,
        zip: zip.trim(),
        country: country,
      });
      if (result?.status === 200) {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          setDisabled(false);
        }, 3000);
      } else {
        setDisabled(false);
        setError("Failed to update profile. Please try again.");
        setTimeout(() => {
          setError("");
        }, 3000);
      }
    } catch (err: any) {
      setDisabled(false);
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError("An error occurred while updating the profile.");
      }
    }
  };

  const fetchStates = async (countryCode: string) => {
    const res = await getStates({ countryCode: countryCode });
    setStateList(res || []);
  };

  const fetchCities = async (stateCode: string) => {
    const res = await getCities({
      countryCode: selectedCountry,
      stateCode: stateCode,
    });
    setCityList(res || []);
  };

  return (
    <>
      <DashboardLayout
        title={"Edit Profile"}
        initialBreadcrumbs={"Extranet"}
        levelBreadcrumbs={""}
        breadcrumbs={"Edit Profile"}
        activeMenu={"overview"}
      >
        {
          !hasFetched ? (
            <>
              <Grid container spacing={2}>
                <Skeleton width="38%" />
                <Skeleton width="100%" />
                <Skeleton width="100%" />
                <Skeleton width="61%" />
              </Grid>
            </>
          ) : (
            <form style={{ padding: 20 }}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <StyledTextField
                    label="Email"
                    value={email}
                    onChange={(e: any) => setEmail(e.target.value)}
                    error={validateFields.email.invalid}
                    helperText={validateFields.email.error}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    variant="outlined"
                    type={'password'}
                    onChange={(e) => setPassword(e.target.value)}
                    error={validateFields.password.invalid}
                    helperText={validateFields.password.error}
                    FormHelperTextProps={{ sx: { marginLeft: '0px' } }}
                    InputProps={{
                      sx: {
                        height: '40px',
                      },
                      endAdornment: (
                        <Box sx={{ ml: 1 }}>
                          <Button
                            onClick={handleChangePassword}
                            disableRipple
                            disableElevation
                            sx={{
                              fontSize: "11px",
                              color: "#d01e42",
                              textDecoration: "underline",
                              //cursor: 'pointer',
                              textTransform: "none",
                              padding: 0,
                              minWidth: "unset",
                              ineHeight: 1,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Change Password
                          </Button>
                        </Box>
                      ),
                    }}
                    InputLabelProps={{
                      sx: {
                        lineHeight: 1,
                        marginTop: '-2px',
                        color: password ? ACTIVE_COLOR : INACTIVE_COLOR,
                        "&.Mui-focused": {
                          color: ACTIVE_COLOR,
                        },
                        "&.MuiInputLabel-shrink": {
                          color: ACTIVE_COLOR,
                        },
                        "&:hover": {
                          color: ACTIVE_COLOR,
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: ACTIVE_COLOR,
                        },
                        "&:hover fieldset": {
                          borderColor: ACTIVE_COLOR,
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={4}>
                  <StyledTextField
                    label="First Name"
                    value={firstName}
                    onChange={(e: any) => setFirstName(e.target.value)}
                    error={validateFields.firstName.invalid}
                    helperText={validateFields.firstName.error}
                  />
                </Grid>
                <Grid size={4}>
                  <StyledTextField
                    label="Last Name"
                    value={lastName}
                    onChange={(e: any) => setLastName(e.target.value)}
                    error={validateFields.lastName.invalid}
                    helperText={validateFields.lastName.error}
                  />
                </Grid>
                <Grid size={4}>
                  <StyledTextField
                    label="Title"
                    value={title}
                    onChange={(e: any) => setTitle(e.target.value)}
                  />
                </Grid>
                <Grid size={4}>
                  <StyledTextField
                    label="Address 1"
                    value={address}
                    onChange={(e: any) => setAddr1(e.target.value)}
                    //error={validateFields.address.invalid}
                    //helperText={validateFields.address.error}
                  />
                </Grid>
                <Grid size={4}>
                  <StyledTextField
                    label="Address 2"
                    value={address2}
                    onChange={(e: any) => setAddr2(e.target.value)}
                  />
                </Grid>
                <Grid size={4}>
                  <StyledTextField
                    label="Phone"
                    value={phone}
                    onChange={(e: any) => setPhone(e.target.value)}
                   // error={validateFields.phone.invalid}
                    //helperText={validateFields.phone.error}
                  />
                </Grid>
                <Grid size={4}>
                  <StyledTextField
                    label="Fax"
                    value={fax}
                    onChange={(e: any) => setFax(e.target.value)}
                  />
                </Grid>
                <Grid size={4}>
                  <FormControl fullWidth>
                    <StyledSelect
                      label="Country"
                      value={country}
                      onChange={(e: any) => {
                        setCountry(e.target.value);
                        setSelectedCountry(e.target.value);
                        fetchStates(e.target.value);
                      }}
                      //error={validateFields.country.invalid}
                      //helperText={validateFields.country.error}
                    >
                      {
                        countries?.map((country: any) => (
                          <MenuItem key={country.iso2} value={country.iso2}>
                            {country.name}
                          </MenuItem>
                        ))
                      }
                    </StyledSelect>
                  </FormControl>
                </Grid>
                <Grid size={4}>
                  <FormControl fullWidth>
                    <StyledSelect
                      label="State"
                      value={state}
                      onChange={(e: any) => {
                        setState(e.target.value);
                        fetchCities(e.target.value);
                      }}
                     // error={validateFields.state.invalid}
                      //helperText={validateFields.state.error}
                    >
                      {
                        stateList?.map((states: any) => (
                          <MenuItem key={states.stateCode} value={states.stateCode}>
                            {states.name}
                          </MenuItem>
                        ))
                      }
                    </StyledSelect>
                  </FormControl>
                </Grid>
                <Grid size={4}>
                  <StyledSelect
                    label="City"
                    value={city}
                    onChange={(e: any) => {
                      setCity(e.target.value);
                    }}
                   // error={validateFields.city.invalid}
                   // helperText={validateFields.city.error}
                  >
                    {
                      cityList?.map((city: any) => (
                        <MenuItem key={city.id} value={city.id}>
                          {city.name}
                        </MenuItem>
                      ))
                    }
                  </StyledSelect>
                </Grid>
                <Grid size={4}>
                  <StyledTextField
                    label="Zip"
                    value={zip}
                    onChange={(e: any) => setZip(e.target.value)}
                   // error={validateFields.zip.invalid}
                   // helperText={validateFields.zip.error}
                  />
                </Grid>
                <Grid size={12}>
                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {error}
                    </Alert>
                  )}
                  {
                    success && (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        User updated successfully!
                      </Alert>
                    )
                  }
                </Grid>
                <Grid size={12}>
                  <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '150px', marginRight: '25px' }} onClick={handleFormAction}>
                    UPDATE
                  </Button>
                  <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '150px' }} onClick={handleCancel}>
                    CANCEL
                  </Button>
                </Grid>
              </Grid>
            </form>
          )
        }
      </DashboardLayout>
    </>
  );
};

export default EditProfile;
